import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Link } from 'react-router';
import clientProfilesImg from '@/assets/landing/client-profiles.png';
import humanDecisionsImg from '@/assets/landing/human-decisions.png';
import instantClientScreeningImg from '@/assets/landing/instant-client-screening.png';
import shareTheRightReportImg from '@/assets/landing/share-the-right-report.png';
import updateClientInfoImg from '@/assets/landing/update-client-info.png';
import watchClientProgressImg from '@/assets/landing/watch-client-progress.png';
import { FeatureRevealCard } from './feature-reveal-card';
import { LandingButton } from './landing-button';

type CardDef = {
  title: string;
  subtitle: string;
  description: string;
  image: string;
  imageBackground: string;
  colSpan: string;
};

const CARDS: CardDef[] = [
  // Row 1 — 2 cards (each half width)
  {
    get title() {
      return t('landing.runInstantClientScreenings');
    },
    get subtitle() {
      return t('landing.seeRiskEvidenceNeverAFakeApprovedLabel');
    },
    get description() {
      return t('landing.buildASimpleSearchBoxEnterAClientName');
    },
    image: instantClientScreeningImg,
    imageBackground: 'linear-gradient(149.86deg, #2C2C52 4.83%, #29564D 87.16%)',
    colSpan: 'col-span-6 md:col-span-3',
  },
  {
    get title() {
      return t('landing.watchClientProgressLive');
    },
    get subtitle() {
      return t('landing.knowExactlyWhenAScreeningFinishesOrAReport');
    },
    get description() {
      return t('landing.yourDashboardUpdatesInRealTimeAsWorkflowsComplete');
    },
    image: watchClientProgressImg,
    imageBackground: 'linear-gradient(149.86deg, #2C2C52 4.83%, #53472C 87.16%)',
    colSpan: 'col-span-6 md:col-span-3',
  },
  // Row 2 — 3 cards (each one-third width)
  {
    get title() {
      return t('landing.shareTheRightReportWithTheRightPerson');
    },
    get subtitle() {
      return t('landing.customerViewVsInternalView');
    },
    get description() {
      return t('landing.generateReportsTailoredToWhoSReadingClientsSee');
    },
    image: shareTheRightReportImg,
    imageBackground: 'linear-gradient(149.86deg, #2C2C52 4.83%, #53472C 87.16%)',
    colSpan: 'col-span-6 md:col-span-2',
  },
  {
    get title() {
      return t('landing.updateClientInfoOnePieceAtATime');
    },
    get subtitle() {
      return t('landing.smallChangesSafeSavesCleanerReviews');
    },
    get description() {
      return t('landing.insteadOfSavingAnEntireProfileAtOnceUpdate');
    },
    image: updateClientInfoImg,
    imageBackground: 'linear-gradient(149.86deg, #2C2C52 4.83%, #29564D 87.16%)',
    colSpan: 'col-span-6 md:col-span-2',
  },
  {
    get title() {
      return t('landing.createCompleteClientProfiles');
    },
    get subtitle() {
      return t('landing.turnAScreeningIntoAPermanentClientFile');
    },
    get description() {
      return t('landing.oneClickTransformsAnySearchIntoAFullClient');
    },
    image: clientProfilesImg,
    imageBackground: 'linear-gradient(149.86deg, #2C2C52 4.83%, #56293A 87.16%)',
    colSpan: 'col-span-6 md:col-span-2',
  },
  // Row 3 — 1 full-width card
  {
    get title() {
      return t('landing.keepHumanDecisionsInControl');
    },
    get subtitle() {
      return t('landing.ravenRecommendsYourTeamDecides');
    },
    get description() {
      return t('landing.buildAReviewScreenThatClearlyShowsTheEvidence');
    },
    image: humanDecisionsImg,
    imageBackground: 'linear-gradient(149.86deg, #2C2C52 4.83%, #56293A 87.16%)',
    colSpan: 'col-span-6',
  },
];

export const RavenApiSection = ({ className }: { className?: string }) => {
  useLocale();

  return (
    <section className={className}>
      <div className="mx-auto max-w-6xl px-6 lg:px-10">
        {/* Badge */}
        <span className="mb-4 inline-block rounded-full bg-[#BEC7F4CC] px-3 py-1 font-medium text-landing-pill-text text-xs">
          {t('workspace.ravenApi')}{' '}
        </span>

        {/* Heading */}
        <h2 className="mb-3 font-bold font-libre-baskerville text-4xl text-[#93A2EC]">
          {t('landing.buildDueDiligenceYourWay')}{' '}
        </h2>

        {/* Subtitle */}
        <p className="mb-10 text-background text-sm">
          {t('landing.buildCustomDashboardsManageClientProfilesGenerateReportsThrough')}{' '}
        </p>

        {/* Cards grid */}
        <div className="grid grid-cols-6 gap-5">
          {CARDS.map((card) => (
            <FeatureRevealCard
              className={card.colSpan}
              description={card.description}
              image={card.image}
              imageBackground={card.imageBackground}
              key={card.title}
              subtitle={card.subtitle}
              title={card.title}
            />
          ))}
        </div>
        <div className="mt-12 flex flex-col items-center justify-center gap-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <h2 className="font-bold font-libre-baskerville text-4xl text-[#93A2EC] tracking-wide">
              {t('landing.readyToStartWithRavenApi')}{' '}
            </h2>
            <p className="text-background">{t('landing.getAccessToYourApiKeyAndAchieveWhat')} </p>
          </div>
          <LandingButton>
            <Link to="/auth/signin">{t('landing.getYourApiKey')}</Link>
          </LandingButton>
        </div>
      </div>
    </section>
  );
};
